import { useAppDispatch, useAppSelector, useInternalRedirect } from '@hooks';
import SearchIcon from '@mui/icons-material/Search';
import { Box, ClickAwayListener, InputAdornment, TextField } from '@mui/material';
import { getSpecialPage } from '@store/app/app.slice';
import { getSearchQuery, setSearchQuery as setSearchQueryAction } from '@store/search/search.slice';
import { updateURLQuery } from '@utils/query.utils';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';

export const ExpandableSearch = () => {
  const dispatch = useAppDispatch();

  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const currentSearchQuery = useAppSelector(getSearchQuery);

  const redirect = useInternalRedirect();
  const searchResultsUrl = useAppSelector(getSpecialPage('searchResultsPage')) || '';
  const inputRef = useRef<HTMLInputElement>();

  const focusInput = debounce(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, 500);

  const handleIconClick = () => {
    setIsExpanded(true);
    focusInput();
  };

  useEffect(() => {
    return () => focusInput.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      dispatch(setSearchQueryAction(searchQuery));
      redirect(searchResultsUrl);
      updateURLQuery(searchQuery);
    }
  };

  useEffect(() => {
    if (currentSearchQuery.length > 0) {
      setSearchQuery(currentSearchQuery);
    }
  }, [currentSearchQuery]);

  const handleClickAway = () => {
    setIsExpanded(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <SearchIcon
          onClick={handleIconClick}
          sx={{
            visibility: isExpanded ? 'hidden' : 'visible',
            '&:hover': {
              cursor: 'pointer',
            },
          }}
        />

        <TextField
          variant='outlined'
          value={searchQuery}
          onChange={handleInputChange}
          size='small'
          autoComplete='off'
          fullWidth
          onKeyDown={handleKeyDown}
          inputRef={inputRef}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end' sx={{ color: '#000' }}>
                <SearchIcon />
              </InputAdornment>
            ),
            sx: {
              borderRadius: '4px',
              '.MuiOutlinedInput-notchedOutline': { border: '0 !important ' },
            },
          }}
          sx={{
            position: 'absolute',
            width: isExpanded ? 200 : 70,
            transition: 'all 0.5s ease-out',
            visibility: isExpanded ? 'visible' : 'hidden',
            right: -14,
            borderBottom: '1px solid',
          }}
        />
      </Box>
    </ClickAwayListener>
  );
};
